import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import generateSectionsData from "./sectionsConfig";
import CollapsiblePanel from "./CollapsiblePanel";
import CollapsibleText from "./CollapsibleText";
import ClickableCheckbox from "./ClickableCheckbox";
import WorkPackageTable from "./WorkPackageTable";
import { getApprovals, getProjectDate, saveProjectDate, reviseDates, getContents, updateSection, approveSection, disproveSection, exportProject, exportGantt } from "./api.js";
import { evaluateProject } from "../EvaluateProject/api.js";
import { showTemporaryMessage } from "./utils.js";
import "./ProjectsWriting.css";
import axios from 'axios';

const ProjectWritingUser = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const projectIdx = state?.project_idx || null;
  const projectTitle = state?.title || "";

  const [projectStartDate, setProjectStartDate] = useState("");
  const [projectDuration, setProjectDuration] = useState("");
  const [savedProjectStartDate, setSavedProjectStartDate] = useState("");
  const [savedProjectDuration, setSavedProjectDuration] = useState("");
  const [isProjectMetaEditing, setIsProjectMetaEditing] = useState(false);

  const [checkboxes, setCheckboxes] = useState({});
  const [selectedSection, setSelectedSection] = useState(null);
  const [openContentKeys, setOpenContentKeys] = useState([]);
  const [sectionsData, setSectionsData] = useState({});
  const [workPackages, setWorkPackages] = useState([]);
  const [showDeleteButtons, setShowDeleteButtons] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editingWPTitle, setEditingWPTitle] = useState(null); // NEW

  const updatedOrderRef = useRef(null);
  const topRef = useRef(null);
  const contentRefs = useRef({});
  const lastWorkPackageRef = useRef(null);
const [loading, setLoading] = useState({ isLoading: false, message: "" });


  const handleApproveAllWPs = async () => {
    const sections = ["TASKS", "WP", "SUBTASKS", "EXPERIMENTS", "OUTCOMES"];
    const results = await Promise.all(
      sections.map(section => approveSection(projectIdx, section))
    );
  
    const allSuccessful = results.every(status => status === "SUCCESS");
  
    if (allSuccessful) {
      showTemporaryMessage("Tüm iş paketi bölümleri onaylandı ✅");
    } else {
      showTemporaryMessage("Bazı bölümler onaylanamadı ❌");
    }
  
    // Refresh UI state
    await fetchProjectData();
  };

  const handleDisapproveAllWPs = async () => {
    const sections = ["TASKS", "WP", "SUBTASKS", "EXPERIMENTS", "OUTCOMES"];
    const results = await Promise.all(
      sections.map(section => disproveSection(projectIdx, section))
    );
  
    const allSuccessful = results.every(status => status === "SUCCESS");
  
    if (allSuccessful) {
      showTemporaryMessage("Onaylar kaldırıldı ❌");
    } else {
      showTemporaryMessage("Bazı onaylar kaldırılamadı ⚠️");
    }
  
    await fetchProjectData();
  };


  const fetchProjectData = async () => {
    try {
      const approvals = await getApprovals(projectIdx);
      setCheckboxes({ ...approvals });

      const dates_data = await getProjectDate(projectIdx);
      setProjectStartDate(dates_data.start_date);
      setProjectDuration(dates_data.duration);

      setLoading({ isLoading: true, message: "Proje verileri yükleniyor..." });
      const sections_content = await getContents(projectIdx);
      setLoading({ isLoading: false, message: "" });
      const baseData = generateSectionsData({});

      Object.keys(baseData).forEach(sectionKey => {
        Object.keys(baseData[sectionKey].content).forEach(contentKey => {
          if (sections_content[contentKey] !== undefined) {
            baseData[sectionKey].content[contentKey].content_text = sections_content[contentKey];
          }
        });
      });
      setSectionsData({ ...baseData }); // Force new reference

      const parseJSONSafe = (input) => {
        try {
          if (typeof input === "string") return JSON.parse(input);
          if (Array.isArray(input)) return input;
        } catch { return []; }
        return [];
      };

      const raw = parseJSONSafe(sections_content.TASKS);
      const explanations = parseJSONSafe(sections_content.WP);
      const activities = parseJSONSafe(sections_content.SUBTASKS);
      const experimentsAll = parseJSONSafe(sections_content.EXPERIMENTS);
      const outcomesAll = parseJSONSafe(sections_content.OUTCOMES);

      const normalized = raw.map(wp => typeof wp === "string" ? { work_package_title: wp } : wp);

      const merged = normalized.map(wp => {
        const title = wp.work_package_title;
        return {
          work_package_title: title,
          start_month: wp.start_month,
          duration: wp.duration,
          explanation: explanations.find(e => e.work_package_title === title)?.explanation || "",
          activities: activities.find(a => a.work_package_title === title)?.activities || [],
          experiments: (() => {
            const rawExp = experimentsAll.find(e => e.work_package_title === title)?.experiments;
            try {
              return typeof rawExp === "string" ? JSON.parse(rawExp.replace(/'/g, '"')) : rawExp || [];
            } catch { return []; }
          })(),
          outcomes: outcomesAll.find(o => o.work_package_title === title)?.outcomes || []
        };
      });

      setWorkPackages([...merged.map(wp => ({ ...wp }))]);
    } catch (error) {
      alert("Veri alınamadı.");
      console.error(error);
    }
  };


  useEffect(() => {
    if (projectIdx) fetchProjectData();
  }, [projectIdx]);

  useEffect(() => {
    if (updatedOrderRef.current) {
      updatedOrderRef.current = null;
    }
  }, [workPackages]);

  useEffect(() => {
    // scroll and open editing mode after WP added
    if (editingWPTitle && lastWorkPackageRef.current) {
      lastWorkPackageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editingWPTitle]);

  const moveWorkPackage = async (index, direction) => {
    const reordered = [...workPackages];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= reordered.length) return;

    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    const reorderedTitles = reordered.map(wp => ({ work_package_title: wp.work_package_title }));

    updatedOrderRef.current = reorderedTitles.map(wp => wp.work_package_title);

    await updateSection(projectIdx, "TASKS", reordered.map(wp => ({work_package_title: wp.work_package_title,
                                                                  start_month: wp.start_month,
                                                                  duration: wp.duration
                                                                  })));
    showTemporaryMessage("Sıralama kaydedildi 💾");
    await fetchProjectData();
  };

  const handleAddWorkPackage = () => {
    const newWP = {
      work_package_title: `Yeni İş Paketi ${workPackages.length + 1}`,
      explanation: "",
      activities: [],
      experiments: [],
      outcomes: [],
      isNew: true
    };
    setEditingWPTitle(newWP.work_package_title); // set editing
    setWorkPackages(prev => [...prev, newWP]);
  };

  const handleDeleteWorkPackage = async (titleToDelete) => {
    
    if (window.confirm("İş paketini silmek istediğinizden emin misiniz?")) {
      const updatedList = workPackages.filter(wp => wp.work_package_title !== titleToDelete);

      setWorkPackages(updatedList);
      const sections = ["TASKS", "WP", "SUBTASKS", "EXPERIMENTS", "OUTCOMES"];
      await Promise.all(sections.map(async section => {
        let payload = updatedList.map(wp => ({
          work_package_title: wp.work_package_title,
          ...(section === "TASKS" && { start_month: wp.start_month, duration: wp.duration }),
          ...(section === "WP" && { explanation: wp.explanation }),
          ...(section === "SUBTASKS" && { activities: wp.activities }),
          ...(section === "EXPERIMENTS" && { experiments: wp.experiments }),
          ...(section === "OUTCOMES" && { outcomes: wp.outcomes })
        }));
        {/* if (section === "TASKS") payload = payload.map(({ work_package_title }) => ({ work_package_title }));*/} 
        await updateSection(projectIdx, section, payload);
      }));
      await fetchProjectData();
    }
  };

  const handleUpdateWorkPackage = async (updatedWP, originalTitle) => { 
    /*alert(JSON.stringify(updatedWP));*/
    const updatedList = workPackages.map(wp =>
      wp.work_package_title === originalTitle ? { ...updatedWP, isNew: false } : wp
    );
    setWorkPackages(updatedList);
    setEditingWPTitle(null);
    const changedSections = ["TASKS", "WP", "SUBTASKS", "EXPERIMENTS", "OUTCOMES"];
    await Promise.all(changedSections.map(async section => {
      let payload = updatedList.map(wp => ({
        work_package_title: wp.work_package_title,
        ...(section === "TASKS" && { start_month: wp.start_month, duration: wp.duration }),
        ...(section === "WP" && { explanation: wp.explanation }),
        ...(section === "SUBTASKS" && { activities: wp.activities }),
        ...(section === "EXPERIMENTS" && { experiments: wp.experiments }),
        ...(section === "OUTCOMES" && { outcomes: wp.outcomes })
      }));
      /*if (section === "TASKS"){
        payload = payload.map(({ work_package_title }) => ({ work_package_title }));
      }*/
      await updateSection(projectIdx, section, payload);
    }));
    return "SUCCESS"; 
  };

  const handleSelectSection = (sectionKey, contentKey) => {
    setSelectedSection(sectionKey);
    setOpenContentKeys([contentKey]);
    if (sectionKey === "C1") {
      // Scroll to top when "İş Paketi" section is selected
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      setTimeout(() => {
        contentRefs.current[contentKey]?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  const handleUpdateSectionData = (contentKey, newText) => {
    setSectionsData(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(sectionKey => {
        if (updated[sectionKey].content[contentKey]) {
          updated[sectionKey].content[contentKey].content_text = newText;
        }
      });
      return updated;
    });
  };

  return (
    <div className="app-container">
        <div
          onClick={() => navigate("/")}
          style={{ cursor: "pointer", display: "inline-block" }}
        >
          <img
            src="/logo_light_unified_noBG.png"
            alt="Logo"
            className="top-logo"
          />
        </div>
      {loading.isLoading && (
      <div className="loading-overlay">
        <div className="spinner" />
        <p>{loading.message}</p>
      </div>
      )}
      <div className="project-screen">
        <div className="project-wrapper">
          
          <div className="project-header">
            <h2>{projectTitle}</h2>
          </div>

          <div className="button-row">
            <button
              onClick={async () => {
                setLoading({ isLoading: true, message: "Proje dosyası oluşturuluyor..." });
                await exportProject(projectIdx);
                setLoading({ isLoading: false, message: "" });
              }}
              className="submit-btn"
            >
              Projeyi İndir
            </button>

            {/*<button
              onClick={() => setShowConfirm(true)}
              className="eval-btn"
            >
              Projeyi Değerlendir
            </button>

            {showConfirm && (
              <div className="modal-overlay">
                <div className="modal">
                  <p>Değerlendirme raporu oluşturma yaklaşık 5 dk sürecektir. Projeyi değerlendirmek istediğinize emin misiniz?</p>
                  <div className="modal-actions">
                    <button
                      onClick={async () => {
                        setShowConfirm(false);
                        setLoading({ isLoading: true, message: "Proje değerlendiriliyor..." });
                        try{
                          await evaluateProject(projectIdx);
                          setLoading({ isLoading: false, message: "" });
                        } catch{
                          setLoading({ isLoading: false, message: "" });
                        }
                      }}
                      className="modal-accept-btn"
                    >
                      Evet
                    </button>
                    <button
                      onClick={() => setShowConfirm(false)}
                      className="modal-reject-btn"
                    >
                      İptal
                    </button>
                  </div>
                </div>
              </div>
            )}*/}
          </div>

          <div className="content-wrapper">
            <div className="side-panel">
              {Object.entries(sectionsData).map(([sectionKey, section]) => (
                <div className="section" key={sectionKey}>
                  <h3>{section.title}</h3>
                  {Object.entries(section.content).map(([contentKey, content]) => (
                    <ClickableCheckbox
                      key={contentKey}
                      label={content.title}
                      checked={checkboxes[contentKey]}
                      onClick={() => handleSelectSection(sectionKey, contentKey)}
                    />
                  ))}
                </div>
              ))}
            </div>

            <div className="main-content"  ref={topRef}>
              {selectedSection === "C1" ? (
                <>
                <div className="project-meta-container">
                  <div className="project-meta-fields">
                    <div className="project-meta-field">
                      <strong>Proje Başlangıç Tarihi:</strong>&nbsp;
                      {isProjectMetaEditing ? (
                        <input
                          type="date"
                          value={projectStartDate}
                          onChange={(e) => setProjectStartDate(e.target.value)}
                          className="project-meta-input"
                        />
                      ) : (
                        <span className="project-meta-static">{projectStartDate || "—"}</span>
                      )}
                    </div>

                    <div className="project-meta-field">
                      <strong>Süre (ay):</strong>&nbsp;
                      {isProjectMetaEditing ? (
                        <input
                          type="number"
                          min="1"
                          value={projectDuration}
                          onChange={(e) => setProjectDuration(e.target.value)}
                          className="project-meta-input"
                        />
                      ) : (
                        <span className="project-meta-static">{projectDuration || "—"}</span>
                      )}
                    </div>
                  </div>
                  <div className="project-meta-buttons">
                    {isProjectMetaEditing ? (
                      <div className="project-meta-editing-btns">
                        <button
                          className="cancel-btn"
                          onClick={() => {
                            setIsProjectMetaEditing(false);
                            setProjectStartDate(savedProjectStartDate);
                            setProjectDuration(savedProjectDuration);
                          }}
                        >
                          VAZGEÇ
                        </button>
                        <button
                          className="edit-btn"
                          onClick={async () => {
                            const isDurationChanged = projectDuration !== savedProjectDuration;
                            const isStartChanged = projectStartDate !== savedProjectStartDate;

                            setLoading({ isLoading: true, message: "İş Paketi Tarihleri Güncelleniyor..." });
                            try {
                              await saveProjectDate(projectIdx, projectStartDate, projectDuration);
                              setSavedProjectStartDate(projectStartDate);
                              setSavedProjectDuration(projectDuration);
                              setIsProjectMetaEditing(false);

                              if (isDurationChanged) {
                                const status = await reviseDates(projectIdx, projectStartDate, projectDuration);
                                if (status === "SUCCESS") {
                                  showTemporaryMessage("Tarihler güncellendi ✅");
                                } else {
                                  showTemporaryMessage("Tarih revizyonu başarısız ❌");
                                }
                              }

                              await fetchProjectData(); // ✅ Refresh UI
                            } catch (err) {
                              console.error("Failed to save or revise:", err);
                              alert("Güncelleme başarısız ❌");
                            } finally {
                            setLoading({ isLoading: false, message: "" });
                            }
                          }}
                        >
                          KAYDET
                        </button>

                      </div>
                    ) : (
                      <button
                        className="edit-btn"
                        onClick={() => {
                          setIsProjectMetaEditing(true);
                          setSavedProjectStartDate(projectStartDate);
                          setSavedProjectDuration(projectDuration);
                        }}
                      >
                        DÜZENLE
                      </button>
                    )}
                  </div>
                </div>

                  <div className="work-package-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: "3%"}}>
                    <div>
                      <button className="add-wp-btn" onClick={handleAddWorkPackage}>➕ İş Paketi Ekle</button>
                      <button className="add-wp-btn" style={{ marginLeft: 10 }} onClick={() => setShowDeleteButtons(x => !x)}>
                        {showDeleteButtons ? "❌ Silme Modunu Kapat" : "🗑️ İş Paketi Sil"}
                      </button>
                      {!checkboxes["TASKS"] ? (
                              <button className="add-wp-btn" style={{ marginLeft: 10 }} onClick={handleApproveAllWPs} >
                                ✅ Tümünü Onayla 
                              </button>
                            ) : (
                              <button className="add-wp-btn" style={{ marginLeft: 10 }} onClick={handleDisapproveAllWPs} >
                                ❌ Onayı Kaldır
                              </button>
                            )}
                    </div>
                    <button
                      className="export-gantt-btn"
                      onClick={async () => {
                        const invalidWPs = workPackages.filter(wp => {
                          const isStartMissing = wp.start_month === undefined || wp.start_month === null || wp.start_month === "";
                          const isDurationMissing = wp.duration === undefined || wp.duration === null || wp.duration === "";
                          const areActivitiesMissing = !Array.isArray(wp.activities) || wp.activities.length === 0;

                          return isStartMissing || isDurationMissing || areActivitiesMissing;
                        });

                        if (invalidWPs.length > 0) {
                          const titles = invalidWPs.map(wp => wp.work_package_title).join("\n ");
                          alert(`Gantt şeması oluşturmak için lütfen eksik veri olan iş paketlerini tamamlayınız. Eksik veri olan iş paketleri: ${titles}`);
                          return;
                        }



                        setLoading({ isLoading: true, message: "Gantt şeması oluşturuluyor..." });
                        try {
                          await exportGantt(projectIdx);
                        } catch (err) {
                          alert("Gantt oluşturulurken bir problem ile karşılaşıldı. Lütfen, daha sonra tekrar deneyin.");
                          console.error(err);
                        } finally {
                          setLoading({ isLoading: false, message: "" });
                        }
                      }}
                    >
                      Gantt Şeması İndir
                    </button>

                  </div>

                  {workPackages.map((wp, idx) => (
                    <div
                      key={wp.work_package_title}
                      className="work-package-wrapper"
                      ref={idx === workPackages.length - 1 ? lastWorkPackageRef : null}
                    >

                      <div className="wp-reorder-controls" style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginBottom: "4px" }}>
                        <button className="reorder-btn" onClick={() => moveWorkPackage(idx, "up")} disabled={idx === 0}>⬆️</button>
                        <button className="reorder-btn" onClick={() => moveWorkPackage(idx, "down")} disabled={idx === workPackages.length - 1}>⬇️</button>
                      
                        {showDeleteButtons && ( //!wp.isNew && 
                        <div style={{ textAlign: "right", marginBottom: 4 }}>
                          <button className="delete-btn small shake" onClick={() => handleDeleteWorkPackage(wp.work_package_title)}>❌</button>
                        </div>
                      )}
                      </div>

                      <WorkPackageTable
                        workPackage={wp}
                        project_idx={projectIdx}
                        project_start_date={projectStartDate}
                        onSave={(updatedWP) => handleUpdateWorkPackage(updatedWP, wp.work_package_title)}
                        isInitiallyEditing={wp.work_package_title === editingWPTitle}
                      />
                    </div>
                  ))}
                </>
              ) : (
                selectedSection && sectionsData[selectedSection] && (
                  <CollapsiblePanel
                    title={sectionsData[selectedSection].title}
                    content={Object.entries(sectionsData[selectedSection].content).map(([key, c]) => {
                      if (!contentRefs.current[key]) contentRefs.current[key] = React.createRef();
                      return (
                        <CollapsibleText
                          key={key}
                          ref={contentRefs.current[key]}
                          section_name={key}
                          title={c.title}
                          project_idx={projectIdx}
                          setCheckboxes={setCheckboxes}
                          checkboxes={checkboxes}
                          maxChars={c.max_chars}
                          content_text={c.content_text}
                          handleUpdateSectionData={handleUpdateSectionData}
                          isOpen={openContentKeys.includes(key)}
                          toggleContentKey={(k, open) =>
                            setOpenContentKeys(prev => open ? [...prev, k] : prev.filter(x => x !== k))
                          }
                          onCloseSection={(k) =>
                            setOpenContentKeys(prev => prev.filter(x => x !== k))
                          }
                        />
                      );
                    })}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectWritingUser;
